(ns quuppa.peweb.peinfo-tags
  (:require [clojure.string :as str]
            [linked.core :as l]
            [quuppa.peweb.tanstack-table :as tanstack-table]
            [quuppa.qsm.permissions-access :as permissions-access]
            [quuppa.qsm.subs :as qsm-subs]
            [quuppa.utils :as utils]
            [reagent.core :as r]
            [re-frame.core :as rf]
            [quuppa.components :as c]
            [quuppa.date-time.core :as date-time]
            [quuppa.peweb.peinfo-utils :as peinfo-utils]
            [quuppa.ui.icons :refer [icon]]
            [quuppa.ui.layout :as layout]
            [quuppa.peweb.peinfo-events :as e]
            [quuppa.peweb.tables :as tables]
            ["react-window" :refer [FixedSizeList]]
            ["react-use-measure$default" :as useMeasure]
            ["react-virtualized-auto-sizer$default" :as AutoSizer]))

;; React-virtuoso would have built-in support for calculating height for every row.
;; Currently, not needed.
;; React-window also allows us to calculate height for each row ourselves.

(defn location-str [[x y z :as loc]]
  (when loc
    (str (.toFixed x 3) ", " (.toFixed y 3) ", " (.toFixed z 3))))

(def position-events-disabled-message
  "QSM is set to not receive location events from the QPE.")

(def default-empty-value-unknown
  [:last-seen :coordinate-system :position :radius :device-address
   :fw-version :battery-alarm :battery-last-low-ts :battery-voltage
   :button-1-state :button-1-ts :rssi :rssi-locator-count :tag-state
   :state-transition :trigger-count :tx-power :tx-rate :io-states
   :config-status :current-config-value-name :current-config-value-channel :key-status])

(def default-empty-value-no-data
  [:location-type :movement-status])

(def default-empty-value-not-set
  [:name :tag-group-name :target-config-value-name :target-config-value-channel])

(defn empty-cell-placeholder [column-key]
  (cond (some #{column-key} default-empty-value-unknown)
        "Unknown"
        (some #{column-key} default-empty-value-no-data)
        "NoData"
        (some #{column-key} default-empty-value-not-set)
        "NotSet"
        :else
        "Unknown"))

(defn temporal-sorting-fn [row-a row-b column-id]
  (let [a (.getValue row-a column-id)
        b (.getValue row-b column-id)]
    (cond
      (and (nil? a) (nil? b)) 0
      (nil? a) 1
      (nil? b) -1
      :else (.compareTo a b))))

(defn sorting-with-empty-placeholder-values [row-a row-b column-id]
  (let [a (.getValue row-a column-id)
        b (.getValue row-b column-id)
        empty-a? (peinfo-utils/safe-empty? a)
        empty-b? (peinfo-utils/safe-empty? b)]
    (cond
      (and empty-a? empty-b?) 0
      empty-a? 1
      empty-b? -1
      (and (number? a) (number? b)) (compare a b)
      (number? a) -1
      (number? b) 1
      :else (compare (str/lower-case (str a)) (str/lower-case (str b))))))

(defn sorting-strings-case-insensitive [row-a row-b column-id]
  (let [a (.getValue row-a column-id)
        b (.getValue row-b column-id)
        empty-a? (peinfo-utils/safe-empty? a)
        empty-b? (peinfo-utils/safe-empty? b)]
    (cond
      (and empty-a? empty-b?) 0
      empty-a? 1
      empty-b? -1
      :else (compare (str/lower-case (str a)) (str/lower-case (str b))))))

(defn vector-first-value-sorting-fn [row-a row-b column-id]
  (let [a (first (.getValue row-a column-id))
        b (first (.getValue row-b column-id))
        empty-a? (nil? a)
        empty-b? (nil? b)]
    (cond
      (and empty-a? empty-b?) 0
      empty-a? 1
      empty-b? -1
      :else (compare a b))))

(defn sorting-mixed-numbers-and-strings [row-a row-b column-id]
  (let [a (.getValue row-a column-id)
        b (.getValue row-b column-id)
        empty-a? (peinfo-utils/safe-empty? a)
        empty-b? (peinfo-utils/safe-empty? b)]
    (cond
      (and empty-a? empty-b?) 0
      empty-a? 1
      empty-b? -1
      (and (number? a) (number? b)) (compare a b)
      (number? a) -1
      (number? b) 1
      :else (compare (str/lower-case (str a)) (str/lower-case (str b))))))

;; - Sorting uses value-fn.
;; - Display uses render-fn value-fn.
;; - Width is "grow factor", kind of like with flexbox.
;;   Final col width is (table-width-px * (col-width / total-col-widths))

;; Note: check qpe-next quuppa.domain/get-tag-ui-configuration-data for old list of tag columns.
(def tag-columns
  (l/map
    :id
    {:value-fn :resolved-id
     :sortable true
     :sortingFn sorting-strings-case-insensitive
     :width 120
     :label "ID"}

    :name
    {:value-fn :name
     :sortable true
     :width 180
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :name)
                    value))
     :sortingFn sorting-with-empty-placeholder-values
     :label "Name"}

    :color
    {:value-fn  :color
     :render-fn (fn [c]
                  [:<>
                   [:div.qi-circle
                    {:style {:color c}
                     :title c}]
                   [:small.ml-2 c]])
     :sortingFn sorting-strings-case-insensitive
     :sortable  true
     :width     40
     :label     "Color"}

    ; :calculation-date
    ; {:value-fn (fn calculation-date-field [v] (-> v :location :calculation-date))
    ;  :render-fn date-time/format-date
    ;  :sortable true
    ;  :width 12
    ;  :label "Date"}

    :last-seen
    {:label "Last seen"
     :sortable true
     :width 180
     :value-fn :last-seen
     :sortingFn temporal-sorting-fn
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :last-seen)
                    (c/date-time-without-utc value)))}

    ;; Same as lastSeenTS?
    :location-ts
    {:value-fn :location-ts
     :render-fn c/timeago
     :sortable true
     :width 180
     :sortingFn temporal-sorting-fn
     :label "Location TS"}

    :location-type
    {:value-fn :location-type
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :location-type)
                    value))
     :sortingFn sorting-with-empty-placeholder-values
     :sortable true
     :width 120
     :label "Location type"}

    :movement-status
    {:value-fn :movement-status
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :movement-status)
                    value))
     :sortingFn sorting-with-empty-placeholder-values
     :sortable true
     :width 120
     :label "Movement status"}

    :coordinate-system
    {:value-fn :cs-name
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :coordinate-system)
                    value))
     :sortingFn sorting-with-empty-placeholder-values
     :sortable true
     :width 120
     :label "Coordinate system"}

    :position
    {:value-fn :position
     :render-fn (fn [value]
                  ;; Position must be a vector [x y z]; keywords like :Position
                  (if (vector? value)
                    (location-str value)
                    (empty-cell-placeholder :position)))
     :sortingFn vector-first-value-sorting-fn
     :width 180
     :sortable true
     :label "Location"}

    :radius
    {:value-fn :radius
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :radius)
                    (str (.toFixed value 2) " m")))
     :sortingFn sorting-mixed-numbers-and-strings
     :width 40
     :sortable true
     :label "Radius"}

    ; [:covariance-matrix #(-> % :location :covariance-matrix str)]

    :device-address
    {:value-fn :device-address
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :device-address)
                    value))
     :sortingFn sorting-with-empty-placeholder-values
     :width 120
     :sortable true
     :label "Device address"}))

(def extra-columns
  (l/map
    :tag-group-name
    {:label "Tag group name"
     :sortable true
     :value-fn :group
     :sortingFn sorting-with-empty-placeholder-values
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :tag-group-name)
                    value))}

    :fw-version
    {:label "Firmware version"
     :sortable true
     :value-fn (fn [tag]
                 (if (string? (:fw-version tag))
                   (utils/parse-number (:fw-version tag))
                   (:fw-version tag)))
     :sortingFn sorting-with-empty-placeholder-values
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :fw-version)
                    value))}

    :battery-alarm
    {:label "Battery alarm"
     :sortable true
     :value-fn :battery-alarm
     :sortingFn sorting-with-empty-placeholder-values
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :battery-alarm)
                    value))}

    :battery-last-low-ts
    {:label "Last battery low TS"
     :value-fn :battery-last-low-date
     :sortable true
     :sortingFn temporal-sorting-fn
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :battery-last-low-ts)
                    (date-time/format-date-time-without-utc value)))}

    :battery-voltage
    {:label "Battery voltage"
     :sortable true
     :value-fn (fn [tag]
                 (if (string? (:battery-voltage tag))
                   (utils/parse-number (:battery-voltage tag))
                   (:battery-voltage tag)))
     :sortingFn sorting-with-empty-placeholder-values
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :battery-voltage)
                    value))}

    :button-1-state
    {:label "Button 1 state"
     :sortable true
     :value-fn :button-1-state
     :sortingFn sorting-with-empty-placeholder-values
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :button-1-state)
                    value))}

    :button-1-ts
    {:label "Button 1 last press time"
     :sortable true
     :sortingFn temporal-sorting-fn
     :value-fn :button-1-last-date
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :button-1-ts)
                    (date-time/format-date-time-without-utc value)))}

    :rssi
    {:label "RSSI"
     :sortable true
     :value-fn :rssi
     :sortingFn sorting-with-empty-placeholder-values
     :render-fn (fn [value]
                  (if (or (peinfo-utils/safe-empty? value) (= value -1000))
                    (empty-cell-placeholder :rssi)
                    value))}

    :rssi-locator-count
    {:label "RSSI Locator count"
     :sortable true
     :value-fn :rssi-locator-count
     :sortingFn sorting-with-empty-placeholder-values
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :rssi-locator-count)
                    value))}

    :tag-state
    {:label "Tag state"
     :sortable true
     :value-fn :tag-state
     :sortingFn sorting-with-empty-placeholder-values
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :tag-state)
                    value))}

    :state-transition
    {:label "State transition"
     :sortable true
     :sortingFn sorting-with-empty-placeholder-values
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :state-transition)
                    value))
     :value-fn :tag-state-transition}

    :trigger-count
    {:label "Trigger count"
     :sortable true
     :value-fn :trigger-count
     :sortingFn sorting-with-empty-placeholder-values
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :trigger-count)
                    value))}

    :tx-power
    {:label "TX power"
     :sortable true
     :value-fn :tx-power
     :sortingFn sorting-with-empty-placeholder-values
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :tx-power)
                    value))}

    :tx-rate
    {:label "TX Rate"
     :sortable true
     :value-fn :tx-rate
     :sortingFn sorting-with-empty-placeholder-values
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :tx-rate)
                    value))}

    :io-states
    {:label "IO States"
     :sortable true
     :sortingFn vector-first-value-sorting-fn
     :value-fn :io-states
     :render-fn (fn [states]
                  (let [joined (when (and (sequential? states) (seq states))
                                 (str/join ", " (remove nil? states)))]
                    (if (peinfo-utils/safe-empty? joined)
                      (empty-cell-placeholder :io-states)
                      joined)))}

    :config-status
    {:label "Config status"
     :sortable true
     :value-fn :config-status
     :sortingFn sorting-with-empty-placeholder-values
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :config-status)
                    value))}
    :key-status
    {:label "Key status"
     :sortable true
     :sortingFn sorting-with-empty-placeholder-values
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :key-status)
                    value))
     :value-fn :key-status}

    :current-config-value-name
    {:label "Current config value name"
     :sortable true
     :value-fn :current-config-value-name
     :sortingFn sorting-with-empty-placeholder-values
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :current-config-value-name)
                    value))}

    :current-config-value-channel
    {:label "Current config value channel"
     :sortable true
     :value-fn :current-config-value-channel
     :sortingFn sorting-with-empty-placeholder-values
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :current-config-value-channel)
                    value))}

    :target-config-value-name
    {:label "Target config value name"
     :sortable true
     :value-fn :target-config-value-name
     :sortingFn sorting-with-empty-placeholder-values
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :target-config-value-name)
                    value))}

    :target-config-value-channel
    {:label "Target config value channel"
     :sortable true
     :value-fn :target-config-value-channel
     :sortingFn sorting-with-empty-placeholder-values
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :target-config-value-channel)
                    value))}))

(def all-columns (into tag-columns extra-columns))
(def default-columns
  (into (l/set) (keys tag-columns)))

(defn column->value [{:keys [value-fn]} tag ctx]
  (if (keyword? value-fn)
    (value-fn tag)
    (value-fn tag ctx)))


;; --- Throttled tag data refresh ---
;; Live tag data can update very frequently. To avoid overwhelming the user,
;; we snapshot the merged tag data at a configurable interval (or manually)
;; and display only the snapshot in the table.

(def refresh-rate-options
  [{:label "1 s"     :value 1000}
   {:label "10 s"    :value 10000}
   {:label "1 min"  :value 60000}
   {:label "Manual" :value :manual}])

(def default-refresh-rate 10000)

(rf/reg-event-db
  ::snapshot-tags
  (fn [db [_ qpe-identity]]
    (let [tags-info     (get-in db [:qpes qpe-identity :tags :info])
          tags-location (get-in db [:qpes qpe-identity :tags :location])
          cs-maps       (get-in db [:qpes qpe-identity :current-project :cs-maps])
          cs-id->name   (into {} (map (juxt :id :name) (vals cs-maps)))
          merged        (->> (merge-with merge tags-info tags-location)
                             vals
                             (filter #(contains? % :resolved-id))
                             (mapv (fn [tag]
                                     (if-let [cs-name (cs-id->name (:cs-id tag))]
                                       (assoc tag :cs-name cs-name)
                                       tag))))]
      (assoc-in db [:ui :tags-snapshot qpe-identity] merged))))

(rf/reg-sub ::tags-snapshot
  (fn [db [_ qpe-identity]]
    (get-in db [:ui :tags-snapshot qpe-identity])))

(rf/reg-event-db
  ::clear-snapshot
  (fn [db [_ qpe-identity]]
    (update-in db [:ui :tags-snapshot] dissoc qpe-identity)))

(rf/reg-event-fx
  ::set-refresh-rate
  (fn [{:keys [db]} [_ rate]]
    {:db (assoc-in db [:user-preferences :tag-refresh-rate] rate)
     :fx [[:update-local-storage {:local-storage-key "user-preferences"
                                  :value {:tag-refresh-rate rate}}]]}))

(rf/reg-sub ::refresh-rate
  (fn [db _]
    (get-in db [:user-preferences :tag-refresh-rate] default-refresh-rate)))

(defn- stop-refresh-loop! [timer-atom]
  (when-let [t @timer-atom]
    (js/clearTimeout t)
    (reset! timer-atom nil)))

(defn- start-refresh-loop!
  "Schedules recurring snapshots using setTimeout.
   Each iteration dispatches a snapshot then schedules the next one."
  [timer-atom qpe-identity rate-ms]
  (stop-refresh-loop! timer-atom)
  (when (number? rate-ms)
    (reset! timer-atom
      (js/setTimeout
        (fn []
          (rf/dispatch [::snapshot-tags qpe-identity])
          (start-refresh-loop! timer-atom qpe-identity rate-ms))
        rate-ms))))

;; If just sort-field or order changes, no need to run filter again.
(rf/reg-sub ::filtered-tags
  (fn [[_ qpe-identity _search-string]]
    (rf/subscribe [::tags-snapshot qpe-identity]))
  (fn sub-filtered-tags [tags-data [_ _qpe-identity search-string]]
    (let [lower-search (str/lower-case (str search-string))]
      (if (and (seq tags-data)
               (seq search-string))
        (->> tags-data
             (filter (fn [tag]
                       (or (str/includes? (str/lower-case (:resolved-id tag)) lower-search)
                           (str/includes? (str/lower-case (str (:name tag))) lower-search)))))
        tags-data))))

(rf/reg-sub ::sorted-tags
  (fn [[_ qpe-identity search-string _sort-field _sort-order]]
    (rf/subscribe [::filtered-tags qpe-identity search-string]))
  (fn sub-sorted-tags [filtered-tags [_ _qpe-identity _search-string sort-field sort-order]]
    (let [column (get all-columns sort-field)]
      (if-let [tags (not-empty filtered-tags)]
        (cond-> (sort-by (fn [tag]
                           (column->value column tag nil))
                         tags)
          (not sort-order) reverse)
        []))))

; (rf/reg-sub ::results
;   (fn [[_ qpe-identity search-string sort-field sort-order _results]]
;     (rf/subscribe [::sorted-tags qpe-identity search-string sort-field sort-order]))
;   (fn sub-results [sorted-tags [_ _qpe-identity _search-string _sort-field _sort-order results]]
;     (vec (take results sorted-tags))))

(rf/reg-sub
  ::tag-by-id
  (fn [[_ qpe-identity]]
    (rf/subscribe [:qpe/data qpe-identity :tags :info]))
  (fn sub-filtered-tags [tags [_ _qpe-identity tag-id]]
    (get tags tag-id)))

(rf/reg-event-fx
  :qpe/set-tag-attribute
  (fn [_ [_ qpe-identity tag-id attribute value]]
    {:fx [[:qpe-dispatch {:qpe-identity qpe-identity
                          :event        [:qpe/set-tag-attribute [tag-id attribute value]]}]]}))

(defn tag-parameters-form [{:keys [qpe-dispatch]} tag]
  (let [current-user @(rf/subscribe [::qsm-subs/user])
        current-user-role (permissions-access/get-user-role current-user)]
    [:div.d-flex.flex-column.p-3
     [:span
      [:strong "Color:"]
      [:span.px-3 (:color tag)]

      [:input {:type      "color"
               :value     (:color tag)
               :on-change #(qpe-dispatch [:qpe/set-tag-attribute (:resolved-id tag) :color (.. % -target -value)])}]]
     (when-not (= current-user-role "member")
       [:div.form-group
        [:strong "Name"]
        [:input.form-control
         {:type "text"
          :default-value (:name tag)
          :on-blur #(qpe-dispatch [:qpe/set-tag-attribute (:resolved-id tag) :name (.. % -target -value)])}]])]))

(defn tag-parameters-sidebar [{:keys [qpe-dispatch] :as props} tag]
  [:div.sidebar
   [:div.sidebar-header
    [:h4.flex-grow-1 (:id tag)]
    [:button
     {:on-click #(qpe-dispatch [:qpe/close-sidebar])}
     [icon :close]]]

   [tag-parameters-form props tag]])

(defn sidebar [{:keys [qpe-subscribe parameters] :as props}]
  (or (when-let [sidebar-type @(qpe-subscribe [:qpe/data :ui :sidebar :type])]
        ;; This is disabled atm
        (case sidebar-type
          :tag-details
          (let [tag-id @(qpe-subscribe [:qpe/data :ui :sidebar :tag-id])
                tag @(qpe-subscribe [::tag-by-id tag-id])]
            {:key tag-id}
            [tag-parameters-sidebar props tag])

          nil))
      (when-let [sidebar-type (-> parameters :query :panel)]
        (case sidebar-type
          :tag-columns
          [tables/columns-sidebar
           props
           {:k :tag-columns
            :all-columns all-columns
            :default-columns default-columns}]
          nil))))

(rf/reg-sub
  ::user-preferences-tag-columns
  (fn [db [_ _]]
    (get-in db [:user-preferences :tag-columns])))

(rf/reg-sub
  ::user-preferences-tag-widths
  (fn [db [_ table-id]]
    (get-in db [:user-preferences :tables table-id :widths])))


(rf/reg-event-fx
  ::set-column-width
  (fn [{:keys [db]} [_ table-id col-id new-width]]
    (let [new-db (assoc-in db [:user-preferences :tables table-id :widths col-id] new-width)
          all-widths (get-in new-db [:user-preferences :tables table-id :widths])]
      {:db new-db
       :fx [[:update-local-storage {:local-storage-key "user-preferences"
                                    :value {:tables {table-id {:widths all-widths}}}}]]})))

(def ^:private data-refresh-interval-ms 10000)

(defn- start-data-refresh-loop!
  "Periodically requests full tag info from the backend via :init/req-tags."
  [timer-atom qpe-identity]
  (stop-refresh-loop! timer-atom)
  (reset! timer-atom
    (js/setTimeout
      (fn []
        (rf/dispatch [:init/req-tags {:identity qpe-identity}])
        (start-data-refresh-loop! timer-atom qpe-identity))
      data-refresh-interval-ms)))

(defn tag-grid [_]
  (let [search-string (r/atom "")
        timer-id (atom nil)
        data-refresh-timer (atom nil)
        prev-rate (atom ::unset)
        awaiting-data? (atom true)
        last-qpe-identity (atom nil)]
    (r/create-class
      {:display-name "tag-grid"
       :component-will-unmount
       (fn [_]
         (stop-refresh-loop! timer-id)
         (stop-refresh-loop! data-refresh-timer)
         (when-let [qid @last-qpe-identity]
           (rf/dispatch [::clear-snapshot qid])))
       :reagent-render
       (fn [{:keys [qpe-subscribe qpe-dispatch qpe-identity parameters] :as props}]
         (reset! last-qpe-identity qpe-identity)
         (let [sort-by-field (-> parameters :query (:sort-field :id))
               sort-order-asc (-> parameters :query (:sort-asc true))
               refresh-rate @(rf/subscribe [::refresh-rate])
               ;; Poll rapidly (200ms) until first data arrives, then use configured rate
               effective-rate (if @awaiting-data? 200 refresh-rate)
               _ (when (not= effective-rate @prev-rate)
                   (reset! prev-rate effective-rate)
                   (rf/dispatch [::snapshot-tags qpe-identity])
                   (start-refresh-loop! timer-id qpe-identity effective-rate))
               _ (when (nil? @data-refresh-timer)
                   (start-data-refresh-loop! data-refresh-timer qpe-identity))
               results @(qpe-subscribe [::sorted-tags @search-string sort-by-field sort-order-asc])
               _ (when (and @awaiting-data? (seq results))
                   (reset! awaiting-data? false))
               selected-columns (or @(rf/subscribe [::user-preferences-tag-columns])
                                    default-columns)
               persisted-widths @(rf/subscribe [::user-preferences-tag-widths :tags])
               manual? (= refresh-rate :manual)]

           [layout/section
            [layout/view-content
             {:sidebar-open (or (:panel (:query parameters))
                                @(qpe-subscribe [:qpe/data :ui :sidebar :type]))
              :scroll-container false}
             [layout/view-header
              {:title "Tags"}]

             [:div.d-flex.flex-row.justify-content-between.py-2
              [:div.d-flex.flex-row.align-items-center.flex-grow-1
               [:span.mr-4
                "Search: "]

               [:input.form-control
                {:type      "text"
                 :style     {:flex "1 1 auto"
                             :min-width "150px"
                             :max-width "200px"}
                 :value     @search-string
                 :on-change #(reset! search-string (.. % -target -value))}]

               [:span.ml-4 "Showing " (count results) " tags"]
               [:span.ml-3 "All times are shown as UTC+00:00"]]

              [:div.btn-toolbar.mx-n2.align-items-center
               [:div.d-flex.align-items-center.mx-2
                [:label.mr-2.mb-0 {:style {:white-space "nowrap"}} "Refresh:"]
                [:select.form-select
                 {:style {:width "70%"}
                  :value (str refresh-rate)
                  :on-change (fn [e]
                               (let [v (.. e -target -value)
                                     rate (if (= v ":manual") :manual (js/parseInt v 10))]
                                 (rf/dispatch [::set-refresh-rate rate])))}
                 (for [{:keys [label value]} refresh-rate-options]
                   ^{:key value}
                   [:option {:value (str value)} label])]]
               (when manual?
                 [:button.btn.btn-outline-secondary.mx-2
                  {:on-click #(rf/dispatch [::snapshot-tags qpe-identity])
                   :title "Refresh tag data"}
                  [icon :arrow-clockwise]])
               [:button.btn.btn-outline-primary.mx-2
                {:on-click #(qpe-dispatch [::e/open-panel :tag-columns])}
                [icon :sliders.mr-3]
                "Columns"]]]

             ;; The check will never be true on QPE-next because the data doesn't exist.
             (when (false? @(qpe-subscribe [:qsm/qpe-configuration-value :enable-position-events]))
               [:div.alert.alert-warning
                {:key "alert"}
                position-events-disabled-message])

             [:div.d-flex.flex-column.scroll-container
              {:style {:flex "1 1 0"
                       :min-height 0}}
              [tanstack-table/virtualized-table
               {:rows results
                :all-columns all-columns
                :selected-columns selected-columns
                :row-height 32
                :active-row-id (:tag-id (:query parameters))
                :props {:qpe-subscribe qpe-subscribe
                        :qpe-dispatch qpe-dispatch}
                :widths persisted-widths
                :on-column-resize (fn [col-id new-width]
                                    (rf/dispatch [::set-column-width :tags col-id new-width]))}]]]
            [sidebar props]]))})))

(def query-params tables/query-params)
